
import React, { useEffect, useMemo, useState } from 'react'

import dayjs from 'dayjs'

import { DatePicker } from 'antd'

import { Controller, useForm } from 'react-hook-form'
import { useAppSelector } from '../../../hooks/redux'
import { type RootState } from '../../../store/store'
import { type IViewing } from '../../../types'
import { getCurrentDubaiDate } from '../../../functions/agent/date/getCurrentDubaiDate'

export default function PickUpKeysTimeSelect({ id, modal, unitID, objectID, row, control, viewingID, register, errors, clearErrors, setValue, trigger }: any): JSX.Element {
    const dateChooseViewing = useAppSelector((state: RootState) => state.modal.dateChooseViewing) as Date
    const viewingDateTime = dayjs(dateChooseViewing)
    const currentTime = getCurrentDubaiDate()
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(viewingDateTime)
    const isPickUp = row.id === 'PickUpDate'

    const units = useAppSelector((state) => state.units.units)
    const allViewings = useAppSelector((state) => state.viewings.viewings)

    const getKeySpans = (): { keysAmount: number, keyDateSpans: dayjs.Dayjs[][] } => {
        const unit = units.find(unitItem => unitItem.id === Number(unitID))
        const keysAmount = unit?.attributes.KeysAmount ?? 1 // TODO fill keys amounts in DB for existing units
        const viewings = allViewings
            .filter((viewing) => unit?.attributes?.Viewings?.data
                .some((viewingItem: IViewing) => viewingItem.id === viewing.id)
            )
        const keyDateSpans: dayjs.Dayjs[][] = viewings
            .map(viewing => {
                const key = viewing?.attributes?.KeyBooking?.data?.attributes
                return [dayjs(key?.PickUpDate), dayjs(key?.DropOutDate)]
            })
            .filter(el => el.every(Boolean))
        return {
            keysAmount,
            keyDateSpans
        }
    }

    const { keysAmount, keyDateSpans } = useMemo(getKeySpans, [units, allViewings])

    const getFreeKeyHours = (timeRange: number[], rawDate: dayjs.Dayjs): number[] => {
        const date = dayjs(rawDate.set('hour', 0).set('minute', 0))
        const sameDayKeys = keyDateSpans.filter(([pickUp, dropOut]) =>
            (date.isSame(pickUp, 'day') || date.isAfter(pickUp)) &&
            (date.isSame(dropOut, 'day') || date.isBefore(dropOut, 'day'))
        )

        return timeRange.filter((hour) => {
            const keysTaken = sameDayKeys.reduce(
                (accumulator, key) => {
                    if (key[0].hour() - 1 < hour && key[1].hour() > hour - 1) {
                        return accumulator + 1
                    }
                    return accumulator
                },
                0
            )
            return keysTaken < keysAmount
        })
    }

    const range = (start: number, end: number): number[] => {
        const result = []
        for (let i = start; i < end; i++) {
            result.push(i)
        }
        return result
    }

    const getEnabledHours = (date: dayjs.Dayjs): number[] => {
        if (isPickUp) {
            if (date.isSame(viewingDateTime, 'day')) {
                return getFreeKeyHours(range(
                    date.isSame(dayjs(), 'day') ? Math.max(dayjs().hour(), 9) : 9,
                    Math.min(
                        viewingDateTime.minute() >= 30 ? viewingDateTime.hour() + 1 : viewingDateTime.hour()
                        , 19
                    )
                ),
                    date
                )
            } else {
                return [18]
            }
        } else {
            if (date.isSame(viewingDateTime, 'day')) {
                return range(
                    date.isSame(dayjs(), 'day') ? Math.max(viewingDateTime.hour(), 9, dayjs().hour()) : Math.max(viewingDateTime.hour(), 9),
                    19
                )
            } else {
                return [9]
            }
        }
    }

    const getDisabledHours = (): number[] => {
        const enabledHours = getEnabledHours(selectedDate)

        return range(0, 24).filter(hour => !enabledHours.includes(hour))
    }

    const getEnabledMinutes = (): number[] => {
        if (isPickUp) {
            if (selectedDate.isSame(viewingDateTime, 'day') && selectedDate.hour() !== 18) {
                return range(0, 60).filter(minute => minute % 30 === 0)
            } else {
                return [0]
            }
        } else {
            if (selectedDate.isSame(viewingDateTime, 'day') && selectedDate.hour() !== 18) {
                return range(0, 60).filter(minute => minute % 30 === 0)
            } else {
                return [0]
            }
        }
    }

    const getDisabledMinutes = (): number[] => {
        const enabledMinutes = getEnabledMinutes()
        return range(0, 60).filter(minute => !enabledMinutes.includes(minute))
    }

    const checkWorkingHours = (): 'In' | 'Before' | 'After' => {
        const workingHours = [9, 18]
        const viewingHour = viewingDateTime.hour()
        if (viewingHour < workingHours[0]) {
            return 'Before'
        } else if (workingHours[1] <= viewingHour) {
            return 'After'
        } else {
            return 'In'
        }
    }

    const workingHours = useMemo(checkWorkingHours, [viewingDateTime])

    return (

        <Controller
            name={row.id}
            control={control}
            // defaultValue={dayjs(dateChooseViewing)}
            rules={row.required === true ? { required: `${String(row.content?.[0])} is required` } : {}}
            render={({ field: { onChange, onBlur, value, name, ref } }) => {
                return (
                    <DatePicker
                        disabledDate={(rawDate: dayjs.Dayjs) => {
                            const date = dayjs(rawDate.set('hour', 0).set('minute', 0))
                            if (getEnabledHours(date).length === 0) { // Disable day if there are no keys left for any free time
                                return true
                            }
                            if ([0, 6].includes(date.day()) || date.isBefore(currentTime, 'day')) { // Disable Saturdays, Sundays and past dates
                                return true
                            }
                            if (isPickUp) {
                                const hasKeyInMorning = getEnabledHours(viewingDateTime).includes(9)
                                const conditions = [
                                    date.isSame(viewingDateTime, 'day') && workingHours !== 'Before', // The day of viewing
                                    date.diff(viewingDateTime, 'day') === -1 && hasKeyInMorning, // The day before viewing
                                    viewingDateTime.day() === 1 && date.diff(viewingDateTime, 'day') === -3 && hasKeyInMorning, // Friday if viewing is on monday
                                    viewingDateTime.day() === 0 && date.diff(viewingDateTime, 'day') === -2 && hasKeyInMorning, // Friday if viewing is on sunday
                                    viewingDateTime.day() === 5 && date.diff(viewingDateTime, 'day') === -1 && hasKeyInMorning // Friday if viewing is on saturday
                                ]
                                return !conditions.some(Boolean)
                            } else {
                                const conditions = [
                                    date.isSame(viewingDateTime, 'day') && workingHours !== 'After', // The day of viewing
                                    date.diff(viewingDateTime, 'day') === 0 && date.isAfter(viewingDateTime, 'day'), // The next day
                                    viewingDateTime.day() === 5 && date.diff(viewingDateTime, 'day') === 2, // Monday if viewing is on friday
                                    viewingDateTime.day() === 6 && date.diff(viewingDateTime, 'day') === 1, // Monday if viewing is on saturday
                                    viewingDateTime.day() === 0 && date.diff(viewingDateTime, 'day') === 0 && date.isAfter(viewingDateTime) // Monday if viewing is on sunday
                                ]
                                return !conditions.some(Boolean)
                            }
                        }}
                        popupClassName="full-screen-time-picker"
                        placeholder={row.content?.[1]}
                        size="large"
                        className="pick-up-keys-picker d-block"
                        inputReadOnly
                        name={name}
                        ref={ref}
                        format={'YYYY/MM/DD HH:mm'}

                        onChange={(rawDate: any) => {
                            const date = dayjs(rawDate.set('hour', 0).set('minute', 0))
                            setSelectedDate(rawDate)
                        }}
                        showTime={{
                            hideDisabledOptions: true,
                            disabledHours: getDisabledHours,
                            disabledMinutes: getDisabledMinutes,
                            format: 'HH:mm'
                        }}
                        showNow={false}
                    />

                )
            }}
        />

    )
}
